import { Component } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { ColDef, RowClickedEvent, themeQuartz, iconSetQuartzLight } from 'ag-grid-community';
import { AG_GRID_LOCALE_DE, AG_GRID_LOCALE_EN, AG_GRID_LOCALE_ES } from '@ag-grid-community/locale';
import { compareGridText } from '../shared/grid-text-comparator';
import { CreateContentTypeDialogComponent } from '../content-type/create-content-type-dialog/create-content-type-dialog.component';
import { ContentTypeSettingsDialogComponent } from '../content-type/content-type-settings-dialog/content-type-settings-dialog.component';
import { ContentTypesSchemaViewDialogComponent } from '../content-type/content-types-schema-view-dialog/content-types-schema-view-dialog.component';
import { ContentTypeDisplayTemplateDialogComponent } from '../content-type/content-type-display-template-dialog/content-type-display-template-dialog.component';
import { SettingsService } from '../service/settings.service';
import { MatDialog } from '@angular/material/dialog';
import { ContentTypeService } from '../service/content-type.service';
import { CommentsService } from '../service/comments.service';
import { TranslateService } from '@ngx-translate/core';
import { ContentType, isDateTimeGetTimeSeconds, removeUndefined } from 'na-lib';
import { DuplicatePassTemplateDialogComponent } from '../duplicate-pass-template-dialog/duplicate-pass-template-dialog.component';
import { environment } from 'src/environments/environment';
import { DeleteContentTypesDialogComponent } from '../delete-content-types-dialog/delete-content-types-dialog.component';
import { PassDefinitionsService } from '../service/pass-definitions.service';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { CoverImageDialogComponent } from '../cover-image-dialog/cover-image-dialog.component';
import { QrCodeDialogComponent } from '../qr-code-dialog/qr-code-dialog.component';
import { supportUrlStructure } from '../supportUrls';
import { AppCheckService } from '../service/app-check.service';
import { firstValueFrom } from 'rxjs';
import { deepCopy } from '@firebase/util';
import { deleteField } from '@angular/fire/firestore';

@Component({
  selector: 'app-dpp-template-list',
  standalone: false,
  templateUrl: './dpp-template-list.component.html',
  styleUrls: ['./dpp-template-list.component.css']
})
export class DPPTemplateListComponent {
  contentTypes: any[] = [];
  passTemplates: any[] = [];
  passDraftIds = new Set<string>();
  private passDraftsByTemplateId = new Map<string, any>();
  selectedContentType?: any;
  templateSearchTerm = '';
  contextUsesAppClip = true;
  readonly paginationPageSizeSelector = [25, 50, 100];
  agGridLocaleText = AG_GRID_LOCALE_EN;
  gridLanguage = 'en';
  readonly detailRowHeight = 176;
  overlayNoRowsTemplate = '';
  readonly dateFilterParams = {
    browserDatePicker: true,
    buttons: ['reset', 'apply'],
    closeOnApply: true,
    comparator: (filterDate: Date, cellValue: any) => this.compareDateFilter(filterDate, cellValue)
  };
  private loadedContextId = '';
  private coverImageRefreshToken = Date.now();
  private readonly urlHost = `${environment.useEmulators ? 'http' : 'https'}://${environment.urlHostName}`;

  readonly theme = themeQuartz.withPart(iconSetQuartzLight).withParams({
    backgroundColor: '#ffffff',
    headerBackgroundColor: '#f8fafc',
    foregroundColor: 'rgb(46, 55, 66)',
    borderColor: '#e4eaf1',
    oddRowBackgroundColor: '#fbfcfe',
    headerFontSize: 14,
    headerFontWeight: 700,
    rowHoverColor: '#fff7f3',
    selectedRowBackgroundColor: '#fff1eb',
    rowBorder: true,
    wrapperBorder: false,
    wrapperBorderRadius: 8,
    fontFamily: 'Roboto, sans-serif'
  });

  contentTypeColumns: ColDef[] = [];

  defaultColDef: ColDef = {
    sortable: true,
    comparator: compareGridText,
    resizable: true,
    filter: true,
    lockVisible: true,
    lockPinned: true,
    mainMenuItems: params => params.defaultItems.filter(item => item !== 'columnChooser' && item !== 'pinSubMenu')
  };
  detailCellRenderer = (params: any) => this.renderTemplateDetail(params.data);

  constructor(
    private settingsService: SettingsService,
    private dialog: MatDialog,
    private router: Router,
    private contentTypeService: ContentTypeService,
    private passService: PassDefinitionsService,
    private commentsService: CommentsService,
    private translate: TranslateService,
    private http: HttpClient,
    private appCheckService: AppCheckService
  ) {
  }

  ngOnInit(): void {
    this.updateLanguage(this.translate.currentLang() ?? 'en');
    this.translate.onLangChange.subscribe(change => this.updateLanguage(change.lang));
    this.settingsService.contextIdChange$.subscribe(contextId => this.loadStudioData(contextId));
    this.settingsService.selectedContextDataChange$.subscribe(context => this.setContextMode(context));
    this.setContextMode(this.settingsService.contextAccount$ || this.settingsService.fieldContexts$?.[0]);
    this.loadStudioData(this.settingsService.contextId$);
  }

  private updateLanguage(language: string): void {
    this.gridLanguage = language.startsWith('de') ? 'de' : language.startsWith('es') ? 'es' : 'en';
    this.agGridLocaleText = this.gridLanguage === 'de'
      ? AG_GRID_LOCALE_DE
      : this.gridLanguage === 'es'
        ? AG_GRID_LOCALE_ES
        : AG_GRID_LOCALE_EN;
    this.overlayNoRowsTemplate = `<span class="grid-empty-overlay">${this.escapeHtml(this.t('contentTypes.noTemplateFound', 'Keine DPP Vorlage gefunden.'))}</span>`;
    this.refreshContentTypeColumns();
    this.refreshPassTemplateRows();
  }

  private loadStudioData(contextId: string, force = false): void {
    if (!contextId || (!force && contextId === this.loadedContextId)) return;
    this.loadedContextId = contextId;
    this.selectedContentType = undefined;
    this.settingsService.getPreformatedContentTypes()
      .subscribe(contentTypes => {
        this.contentTypes = (contentTypes || []).map(contentType => this.toContentTypeRow(contentType));
        this.refreshPassTemplateRows();
      });
    this.settingsService.observeContextsPassDefinitionsFirestoreWithId(this.settingsService.contextId$)
      .subscribe(passTemplates => {
        this.passTemplates = passTemplates || [];
        this.refreshPassTemplateRows();
      });
    this.passService.observePassDefinitionDrafts(this.settingsService.contextId$)
      .subscribe(drafts => {
        const entries = (drafts || []).filter(draft => !!draft?.id);
        this.passDraftIds = new Set(entries.map(draft => draft.id));
        this.passDraftsByTemplateId = new Map(entries.map(draft => [draft.id, draft]));
        this.refreshPassTemplateRows();
      });
  }

  createContentType(): void {
    const ref = this.dialog.open(CreateContentTypeDialogComponent, {
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      maxHeight: '100vh',
      data: { createPassTemplate: true, schemaWizard: true }
    });
    ref.afterClosed().subscribe(result => {
      if (!result?.[0]) return;
      this.loadStudioData(this.settingsService.contextId$, true);
      if (result?.[3]?.openPassTemplate && result?.[2]) this.router.navigateByUrl(result[2]);
    });
  }

  editContentType(): void {
    if (!this.selectedContentType) return;
    this.openContentTypeSettings(this.selectedContentType.id, this.selectedContentType);
  }

  editDisplayTemplate(contentType = this.selectedContentType): void {
    if (!contentType?.id) return;
    const ref = this.dialog.open(ContentTypeDisplayTemplateDialogComponent, {
      width: '1120px',
      maxWidth: '96vw',
      maxHeight: '90vh',
      data: { contentType, id: contentType.id },
      disableClose: true
    });
    ref.afterClosed().subscribe(async result => {
      if (!result?.[0]) return;
      const updatedContentType = new ContentType();
      updatedContentType.setFromAny({ ...contentType, displaySections: result[1] });
      try {
        this.commentsService.progressSpin.emit(true);
        await this.contentTypeService.updateContentType(updatedContentType, contentType);
        this.commentsService.addSnackBar.emit(this.translate.instant('SnackBarConstants.UPDATE_OK'));
        this.loadStudioData(this.settingsService.contextId$, true);
      } catch {
        this.commentsService.addSnackBar.emit(this.translate.instant('SnackBarConstants.UPDATE_FAILED'));
      } finally {
        this.commentsService.progressSpin.emit(false);
      }
    });
  }

  editSchema(): void {
    if (!this.selectedContentType) return;
    const ref = this.dialog.open(ContentTypesSchemaViewDialogComponent, {
      width: '100vw', maxWidth: '100vw', height: '100dvh', maxHeight: '100dvh',
      panelClass: 'schema-fullscreen-dialog',
      data: { contentType: this.selectedContentType, id: this.selectedContentType.id }
    });
    ref.afterClosed().subscribe(async result => {
      if (!result?.[0]) return;
      const updatedContentType = new ContentType();
      updatedContentType.setFromAny({ ...this.selectedContentType, schema: result[1], metaTemplate: result[2] });
      (updatedContentType as ContentType & { metaTemplate?: unknown }).metaTemplate =
        result[2]?.fields?.length ? result[2] : deleteField();
      try {
        this.commentsService.progressSpin.emit(true);
        await this.contentTypeService.updateContentType(updatedContentType, this.selectedContentType);
        this.selectedContentType = {
          ...this.selectedContentType,
          schema: result[1],
          metaTemplate: result[2]?.fields?.length ? result[2] : undefined
        };
        const contentTypeIndex = this.contentTypes.findIndex(item => item.id === this.selectedContentType?.id);
        if (contentTypeIndex !== -1) this.contentTypes[contentTypeIndex] = this.selectedContentType;
        this.commentsService.addSnackBar.emit(this.translate.instant('SnackBarConstants.UPDATE_OK'));
      } catch {
        this.commentsService.addSnackBar.emit(this.translate.instant('SnackBarConstants.UPDATE_FAILED'));
      } finally {
        this.commentsService.progressSpin.emit(false);
      }
    });
  }

  private openContentTypeSettings(contentTypeId: string, contentType?: any): void {
    this.dialog.open(ContentTypeSettingsDialogComponent, {
      width: '720px', maxWidth: '96vw',
      data: { contentTypeId, contentType }
    });
  }

  async createPassTemplate(contentType = this.selectedContentType): Promise<void> {
    if (!contentType?.id) return;
    if (contentType.passTemplate || contentType.passTemplateId) {
      this.openPassTemplateForContentType(contentType);
      return;
    }
    try {
      this.commentsService.progressSpin.emit(true);
      const result = await this.contentTypeService.createPassTemplateForContentType(contentType);
      this.router.navigate(['home', this.settingsService.contextId$, 'contentTypes', contentType.id, 'passTemplate', result.passTemplateId]);
    } catch (error) {
      this.commentsService.addSnackBar.emit(this.t('contentTypes.createDesignFailed', 'Die Design Vorlage konnte nicht erstellt werden.'));
    } finally {
      this.commentsService.progressSpin.emit(false);
    }
  }

  duplicateContentType(contentType = this.selectedContentType): void {
    if (this.hasActiveDraft(contentType)) {
      this.commentsService.addSnackBar.emit(this.t('contentTypes.draftActionUnavailable', 'Duplicate and export are unavailable while an unpublished draft exists.'));
      return;
    }
    if (!contentType?.passTemplateId) {
      this.commentsService.addSnackBar.emit(this.t('contentTypes.designRequiredForDuplicate', 'Erstelle zuerst eine Design Vorlage, bevor du diese Vorlage duplizierst.'));
      return;
    }
    const ref = this.dialog.open(DuplicatePassTemplateDialogComponent, {
      width: '520px',
      maxWidth: '96vw',
      data: {
        passTemplateId: contentType.passTemplateId,
        oldDisplayName: contentType.displayName,
        contextid: this.settingsService.contextId$,
        contentTypeId: contentType.id
      }
    });
    ref.afterClosed().subscribe(() => this.loadStudioData(this.settingsService.contextId$, true));
  }

  deleteContentType(contentType = this.selectedContentType): void {
    if (!contentType?.id) return;
    const ref = this.dialog.open(DeleteContentTypesDialogComponent, {
      data: { values: contentType }
    });
    ref.afterClosed().subscribe(result => {
      if (result?.[0]) this.loadStudioData(this.settingsService.contextId$, true);
    });
  }

  async exportTemplate(contentType = this.selectedContentType): Promise<void> {
    if (!contentType?.id) return;
    if (this.hasActiveDraft(contentType)) {
      this.commentsService.addSnackBar.emit(this.t('contentTypes.draftActionUnavailable', 'Duplicate and export are unavailable while an unpublished draft exists.'));
      return;
    }
    try {
      this.commentsService.progressSpin.emit(true);
      const passTemplate = contentType.passTemplateId
        ? await this.settingsService.getPassDefinitionId(contentType.passTemplateId)
        : contentType.passTemplate;
      const children = contentType.passTemplateId
        ? await this.passService.getPassChildren(contentType.passTemplateId)
        : [];
      const zip = new JSZip();
      const cleanContentType = this.cleanTemplateExport(contentType, ['passTemplate']);
      const cleanPassTemplate = this.cleanTemplateExport(passTemplate || {});
      const exportAssets = this.collectTemplateAssets(cleanPassTemplate, children || [], contentType.passTemplateId || cleanPassTemplate?.id || '');
      zip.file('contentType.json', JSON.stringify(cleanContentType, null, 2));
      zip.file('template.json', JSON.stringify(cleanPassTemplate, null, 2));
      if (children?.length) zip.file('children.json', JSON.stringify(children, null, 2));
      await this.addAssetsToZip(zip, exportAssets);
      const blob = await zip.generateAsync({ type: 'blob' });
      saveAs(blob, `${this.sanitizeFileName(contentType.displayName || 'dpp-vorlage')}.nvpt`);
      this.commentsService.addSnackBar.emit(this.t('contentTypes.exportSuccess', 'DPP Vorlage exportiert.'));
    } catch (error) {
      console.error(error);
      this.commentsService.addSnackBar.emit(this.t('contentTypes.exportFailed', 'Die DPP Vorlage konnte nicht exportiert werden.'));
    } finally {
      this.commentsService.progressSpin.emit(false);
    }
  }

  async importTemplateFile(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    try {
      this.commentsService.progressSpin.emit(true);
      const imported = await this.readTemplateImport(file);
      if (!imported.contentType || !imported.passTemplate) {
        this.commentsService.addSnackBar.emit(this.t('contentTypes.importIncomplete', 'Die Datei enthält keine vollständige DPP Vorlage.'));
        return;
      }
      const result = await this.contentTypeService.importContentype(imported.contentType, imported.passTemplate);
      if (imported.children?.length && result?.passTemplateId) {
        await this.passService.setPassChildren(result.passTemplateId, imported.children.map(child => ({
          ...child,
          contextId: this.settingsService.contextId$,
          contentType: result.contentTypeId
        })));
      }
      this.commentsService.addSnackBar.emit(this.t('contentTypes.importSuccessTemplate', 'DPP Vorlage importiert.'));
      this.loadStudioData(this.settingsService.contextId$, true);
    } catch (error) {
      console.error(error);
      this.commentsService.addSnackBar.emit(this.t('contentTypes.importFailed', 'Die DPP Vorlage konnte nicht importiert werden.'));
    } finally {
      input.value = '';
      this.commentsService.progressSpin.emit(false);
    }
  }

  openPassTemplateForContentType(contentType = this.selectedContentType): void {
    const passTemplate = contentType?.passTemplate || this.passTemplates.find(template => template.id === contentType?.passTemplateId || template.contentType === contentType?.id);
    const passTemplateId = passTemplate?.id || contentType?.passTemplateId;
    if (!contentType?.id || !passTemplateId) return;
    this.router.navigate(['home', this.settingsService.contextId$, 'contentTypes', contentType.id, 'passTemplate', passTemplateId]);
  }

  onContentTypeSelected(event: RowClickedEvent): void {
    const target = event.event?.target as HTMLElement | undefined;
    if (target?.closest?.('button, [data-detail-action]')) return;
    if (target?.closest?.('.ag-group-expanded, .ag-group-contracted, .ag-icon-tree-open, .ag-icon-tree-closed')) return;
    this.selectedContentType = event.data;
    if (!event.node.expanded) this.closeOtherDetailRows(event.api, event.node);
    event.node.setExpanded(!event.node.expanded);
  }

  onDetailRowToggled(event: any): void {
    if (event.node?.expanded) this.closeOtherDetailRows(event.api, event.node);
  }

  private closeOtherDetailRows(api: any, activeNode: any): void {
    api.forEachNode((node: any) => {
      if (node !== activeNode && node.expanded) node.setExpanded(false);
    });
  }

  onTemplateGridClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | undefined;
    const actionElement = target?.closest?.('[data-detail-action]') as HTMLElement | undefined;
    if (!actionElement) return;
    const action = actionElement.getAttribute('data-detail-action') || '';
    const contentTypeId = actionElement.getAttribute('data-content-type-id') || '';
    const row = this.contentTypes.find(contentType => contentType.id === contentTypeId);
    if (!row) return;
    this.selectedContentType = row;
    if (action === 'schema') this.editSchema();
    if (action === 'design') this.openPassTemplateForContentType(row);
    if (action === 'preview') this.previewPassTemplate(row);
    if (action === 'generate') this.generateDpps(row);
    if (action === 'settings') this.editContentType();
    if (action === 'display') this.editDisplayTemplate(row);
    if (action === 'duplicate') this.duplicateContentType(row);
    if (action === 'delete') this.deleteContentType(row);
    if (action === 'export') this.exportTemplate(row);
    if (action === 'cover') this.openCoverImage(row);
    if (action === 'create-design') this.createPassTemplate(row);
  }

  onTemplateSearch(value: string): void {
    this.templateSearchTerm = value;
  }

  clearTemplateSearch(): void {
    this.templateSearchTerm = '';
  }

  generateDpps(contentType = this.selectedContentType): void {
    this.router.navigate(['home', this.settingsService.contextId$, 'generate-tridys'], {
      queryParams: contentType?.id ? { contentTypeId: contentType.id } : undefined
    });
  }

  openCoverImage(contentType = this.selectedContentType): void {
    if (!contentType?.id) return;
    const ref = this.dialog.open(CoverImageDialogComponent, {
      disableClose: true,
      data: {
        contextId: this.settingsService.contextId$,
        contentTypeId: contentType.id,
        contentType
      },
      width: '1000px',
      maxWidth: '90vw',
      panelClass: 'custom-dialog-container',
      position: { right: '0' }
    });
    ref.afterClosed().subscribe(result => {
      if (result?.[0]) this.coverImageRefreshToken = Date.now();
      this.loadStudioData(this.settingsService.contextId$, true);
    });
  }

  previewPassTemplate(contentType = this.selectedContentType): void {
    const passTemplate = contentType?.passTemplate || this.passTemplates.find(template => template.id === contentType?.passTemplateId || template.contentType === contentType?.id);
    const passTemplateId = passTemplate?.id || contentType?.passTemplateId;
    const draft = passTemplateId ? this.passDraftsByTemplateId.get(passTemplateId) : undefined;
    if (!contentType?.id || !passTemplateId) {
      this.commentsService.addSnackBar.emit(this.t('contentTypes.designRequiredForPreview', 'Erstelle zuerst eine Design Vorlage, bevor du die Vorschau öffnest.'));
      return;
    }
    this.dialog.open(QrCodeDialogComponent, {
      data: {
        contentType: {
          contentType: passTemplateId,
          contextId: this.settingsService.contextId$
        },
        id: contentType.id,
        nfcPrefix: this.contextUsesAppClip ? contentType.nfcURLPrefix : undefined,
        name: passTemplate?.name || contentType.passTemplateName || contentType.displayName,
        hasDraft: !!draft,
        draftId: draft?.draftId || draft?.id || passTemplateId
      }
    });
  }

  private setContextMode(context: any): void {
    const usesAppClip = !String(context?.customDPPHost || '').trim();
    if (this.contextUsesAppClip === usesAppClip) return;
    this.contextUsesAppClip = usesAppClip;
    this.refreshContentTypeColumns();
  }

  private refreshContentTypeColumns(): void {
    this.contentTypeColumns = this.withContextColumns();
  }

  private withContextColumns(): ColDef[] {
    const [displayNameColumn, schemaColumn, passTemplateColumn, ...restColumns] = this.getContentTypeBaseColumns();
    return this.contextUsesAppClip
      ? [displayNameColumn, schemaColumn, passTemplateColumn, this.getCoverColumn(), ...restColumns]
      : [displayNameColumn, schemaColumn, passTemplateColumn, ...restColumns];
  }

  private getContentTypeBaseColumns(): ColDef[] {
    return [
      {
        field: 'displayName',
        headerName: this.t('contentTypes.template', 'Name'),
        minWidth: 260,
        filter: 'agTextColumnFilter',
        flex: 1.2,
        cellRenderer: 'agGroupCellRenderer',
        cellRendererParams: { innerRenderer: (params: any) => this.renderTemplateName(params.data) }
      },
      { field: 'schemaStatus', headerName: this.t('contentTypes.schema', 'Datenstruktur'), width: 172, filter: 'agTextColumnFilter', cellRenderer: params => this.renderStatusAction(params.value, params.data?.schemaFieldCount ? 'ready' : 'missing', 'schema', params.data?.id, params.data?.schemaFieldCount ? this.t('contentTypes.editSchema', 'Struktur bearbeiten') : this.t('contentTypes.createSchema', 'Struktur erstellen')) },
      { field: 'passTemplateName', headerName: this.t('contentTypes.designTemplate', 'Design Vorlage'), minWidth: 205, flex: 1, cellRenderer: params => this.renderPassTemplateAction(params) },
      { field: 'creationDate', headerName: this.t('creation_date', 'Erstellt am'), width: 165, filter: 'agDateColumnFilter', filterParams: this.dateFilterParams, cellDataType: false, comparator: (left, right) => this.compareDates(left, right), valueFormatter: params => this.formatDate(params.value) },
      { field: 'timeStamp', headerName: this.t('last_update', 'Geändert am'), width: 165, sort: 'desc', filter: 'agDateColumnFilter', filterParams: this.dateFilterParams, cellDataType: false, comparator: (left, right) => this.compareDates(left, right), valueFormatter: params => this.formatDate(params.value) },
    ];
  }

  private getCoverColumn(): ColDef {
    return { field: 'coverImage', headerName: this.t('contentTypes.coverImage', 'Titelbild'), width: 116, sortable: false, filter: false, cellRenderer: params => this.renderCoverImage(params.data) };
  }

  private refreshPassTemplateRows(): void {
    const typesById = new Map(this.contentTypes.map(contentType => [contentType.id, contentType]));
    this.contentTypes = this.contentTypes.map(contentType => {
      const passTemplate = this.passTemplates.find(template => template.id === contentType.passTemplateId || template.contentType === contentType.id);
      const passTemplateId = passTemplate?.id || contentType.passTemplateId;
      const hasDraft = !!passTemplateId && this.passDraftIds.has(passTemplateId);
      return {
        ...contentType,
        passTemplate,
        passTemplateName: passTemplate?.name || (contentType.passTemplateId ? this.t('contentTypes.linkedTemplate', 'Verknüpfte Vorlage') : this.t('contentTypes.noTemplate', 'Keine Vorlage')),
        hasDraft,
        displayTemplateReady: this.hasDisplayTemplate(contentType),
        displayTemplateStatus: this.hasDisplayTemplate(contentType) ? `${contentType.displaySections?.length || 0} ${this.t('contentTypes.sections', 'Bereiche')}` : this.t('contentTypes.notConfigured', 'Nicht eingerichtet'),
        schemaStatus: contentType.schemaFieldCount ? `${contentType.schemaFieldCount} ${this.t('contentTypes.fields', 'Felder')}` : this.t('contentTypes.noDataStructure', 'Keine Datenstruktur'),
        templateReady: Boolean(contentType.schemaFieldCount && (passTemplate || contentType.passTemplateId)),
        templateStatus: contentType.schemaFieldCount && (passTemplate || contentType.passTemplateId) ? this.t('contentTypes.ready', 'Bereit') : this.t('contentTypes.incomplete', 'Unvollständig'),
        timeStamp: this.getLatestTimestamp(contentType?.timeStamp || contentType?.creationDate, passTemplate?.timeStamp || passTemplate?.creationDate)
      };
    });
    this.passTemplates = this.passTemplates.map(template => {
      const linkedContentType = typesById.get(template.contentType) || this.contentTypes.find(contentType => contentType.passTemplateId === template.id);
      return {
        ...template,
        contentType: template.contentType || linkedContentType?.id,
        contentTypeName: linkedContentType?.displayName || this.t('contentTypes.notLinked', 'Nicht verknüpft')
      };
    });
  }

  private toContentTypeRow(contentType: any): any {
    const schemaFieldCount = this.countSchemaFields(contentType?.schema?.properties || []);
    return {
      ...contentType,
      schemaFieldCount,
      schemaStatus: schemaFieldCount ? `${schemaFieldCount} ${this.t('contentTypes.fields', 'Felder')}` : this.t('contentTypes.noDataStructure', 'Keine Datenstruktur'),
      displayTemplateReady: this.hasDisplayTemplate(contentType),
      displayTemplateStatus: this.hasDisplayTemplate(contentType) ? `${contentType.displaySections?.length || 0} ${this.t('contentTypes.sections', 'Bereiche')}` : this.t('contentTypes.notConfigured', 'Nicht eingerichtet'),
      templateStatus: this.t('contentTypes.incomplete', 'Unvollständig')
    };
  }

  private hasActiveDraft(contentType: any): boolean {
    return !!contentType?.hasDraft || (!!contentType?.passTemplateId && this.passDraftIds.has(contentType.passTemplateId));
  }

  private renderTemplateDetail(row: any): string {
    const designReady = Boolean(row?.passTemplate || row?.passTemplateId);
    const schemaReady = Boolean(row?.schemaFieldCount);
    const hasDraft = this.hasActiveDraft(row);
    const rowId = this.escapeHtml(row?.id || '');
    const draftActionHint = this.escapeHtml(this.t('contentTypes.draftActionUnavailable', 'Duplicate and export are unavailable while an unpublished draft exists.'));
    const draftDisabled = hasDraft ? `disabled aria-disabled="true" title="${draftActionHint}"` : '';
    const exportAction = hasDraft
      ? `<span class="detail-action-tooltip" title="${draftActionHint}"><button type="button" class="detail-utility-button" data-detail-action="export" data-content-type-id="${rowId}" ${draftDisabled}><span class="material-icons">download</span>${this.t('contentTypes.export', 'Export')}</button></span>`
      : `<button type="button" class="detail-utility-button" data-detail-action="export" data-content-type-id="${rowId}"><span class="material-icons">download</span>${this.t('contentTypes.export', 'Export')}</button>`;
    const duplicateAction = hasDraft
      ? `<span class="detail-action-tooltip" title="${draftActionHint}"><button type="button" class="detail-utility-button" data-detail-action="duplicate" data-content-type-id="${rowId}" ${draftDisabled}><span class="material-icons">content_copy</span>${this.t('contentTypes.duplicate', 'Duplizieren')}</button></span>`
      : `<button type="button" class="detail-utility-button" data-detail-action="duplicate" data-content-type-id="${rowId}" ${designReady ? '' : 'disabled'}><span class="material-icons">content_copy</span>${this.t('contentTypes.duplicate', 'Duplizieren')}</button>`;
    const coverUtility = this.contextUsesAppClip
      ? `<button type="button" class="detail-utility-button" data-detail-action="cover" data-content-type-id="${rowId}"><span class="material-icons">image</span>${this.t('contentTypes.editCoverImage', 'Titelbild anpassen')}</button>`
      : '';
    return `
      <div class="template-detail">
        <div class="detail-primary-grid">
          <section class="detail-work-card structure">
            <div class="detail-card-header">
              <span class="detail-icon material-icons">account_tree</span>
              <div>
                <span class="detail-label">${this.t('contentTypes.structure', 'Struktur')}</span>
                <strong>${this.escapeHtml(row?.schemaStatus || this.t('contentTypes.noDataStructure', 'Keine Datenstruktur'))}</strong>
                <small>${schemaReady ? `${this.t('contentTypes.dataValidation', 'Datenprüfung')}: ${this.formatSchemaMode(row?.schemaMode)}` : this.t('contentTypes.dataFieldsMissing', 'Datenfelder fehlen noch')}</small>
              </div>
            </div>
            <button type="button" class="detail-card-button" data-detail-action="schema" data-content-type-id="${rowId}">
              <span class="material-icons">${schemaReady ? 'edit_note' : 'add'}</span>${schemaReady ? this.t('contentTypes.editSchema', 'Struktur bearbeiten') : this.t('contentTypes.createSchema', 'Struktur erstellen')}
            </button>
          </section>

          <section class="detail-work-card design">
            <div class="detail-card-header">
              <span class="detail-icon material-icons">widgets</span>
              <div>
                <span class="detail-label">${this.t('contentTypes.design', 'Design')} <em>(${this.t('contentTypes.optional', 'optional')})</em></span>
                <strong>${this.escapeHtml(row?.passTemplateName || this.t('contentTypes.noTemplate', 'Keine Vorlage'))}</strong>
                <small>${row?.hasDraft ? this.t('contentTypes.unpublishedDraft', 'Unveröffentlichter Entwurf vorhanden') : (designReady ? `${this.t('contentTypes.changed', 'Geändert')} ${this.escapeHtml(this.formatDate(row?.timeStamp) || '-')}` : this.t('contentTypes.designTemplateMissing', 'Design Vorlage fehlt noch'))}</small>
              </div>
            </div>
            <div class="detail-card-actions">
              <button type="button" class="detail-card-button primary" data-detail-action="${designReady ? 'design' : 'create-design'}" data-content-type-id="${rowId}">
                <span class="material-icons">${designReady ? 'brush' : 'add'}</span>${designReady ? this.t('contentTypes.edit', 'Bearbeiten') : this.t('contentTypes.create', 'Erstellen')}
              </button>
              <button type="button" class="detail-card-button" data-detail-action="preview" data-content-type-id="${rowId}" ${designReady ? '' : 'disabled'}>
                <span class="material-icons">visibility</span>${this.t('contentTypes.preview', 'Vorschau')}
              </button>
            </div>
          </section>

          <section class="detail-work-card generate">
            <div class="detail-card-header">
              <span class="detail-icon material-icons">qr_code_2</span>
              <div>
                <span class="detail-label">DPPs</span>
                <strong>${this.t('contentTypes.generateDpps', 'DPPs generieren')}</strong>
                <small>${this.t('contentTypes.generateDppsDescription', 'Aus dieser Vorlage neue DPPs generieren')}</small>
              </div>
            </div>
            <button type="button" class="detail-card-button primary generate" data-detail-action="generate" data-content-type-id="${rowId}">
              <span class="material-icons">play_arrow</span>${this.t('contentTypes.generate', 'Generieren')}
            </button>
          </section>
        </div>

        <div class="detail-utility-row" aria-label="${this.t('contentTypes.moreOptions', 'Weitere Optionen')}">
          ${coverUtility}
          <button type="button" class="detail-utility-button" data-detail-action="settings" data-content-type-id="${rowId}"><span class="material-icons">settings</span>${this.t('contentTypes.settings', 'Einstellungen')}</button>
          ${exportAction}
          ${duplicateAction}
          <button type="button" class="detail-utility-button danger" data-detail-action="delete" data-content-type-id="${rowId}"><span class="material-icons">delete</span>${this.t('contentTypes.delete', 'Löschen')}</button>
        </div>
      </div>
    `;
  }

  private renderStatusPill(label: string, state: 'ready' | 'missing'): string {
    const icon = state === 'ready' ? 'check_circle' : 'error_outline';
    return `<span class="grid-status-pill ${state}"><span class="material-icons">${icon}</span>${this.escapeHtml(label || (state === 'ready' ? this.t('contentTypes.ready', 'Bereit') : this.t('contentTypes.missing', 'Fehlt')))}</span>`;
  }

  private renderPassTemplateAction(params: any): string {
    const hasDesign = !!(params.data?.passTemplate || params.data?.passTemplateId);
    return `
      <span class="grid-action-cell design-template-cell">
        <span class="grid-template-text">${this.escapeHtml(params.value || (hasDesign ? this.t('contentTypes.designTemplate', 'Design Vorlage') : this.t('contentTypes.noTemplate', 'Keine Vorlage')))}</span>
        ${params.data?.hasDraft ? `<span class="grid-draft-chip">${this.t('contentTypes.draft', 'Entwurf')}</span>` : ''}
        <button type="button" class="grid-cell-edit" data-detail-action="${hasDesign ? 'design' : 'create-design'}" data-content-type-id="${this.escapeHtml(params.data?.id || '')}" aria-label="${hasDesign ? this.t('contentTypes.editDesign', 'Design bearbeiten') : this.t('contentTypes.createDesign', 'Design erstellen')}">
          <span class="material-icons">${hasDesign ? 'edit' : 'add'}</span>
        </button>
      </span>
    `;
  }

  private renderStatusAction(label: string, state: 'ready' | 'missing', action: string, rowId: string, ariaLabel: string, hasDraft = false): string {
    return `
      <span class="grid-action-cell">
        ${this.renderStatusPill(label, state)}
        ${hasDraft ? `<span class="grid-draft-chip">${this.t('contentTypes.draft', 'Entwurf')}</span>` : ''}
        <button type="button" class="grid-cell-edit" data-detail-action="${this.escapeHtml(action)}" data-content-type-id="${this.escapeHtml(rowId || '')}" aria-label="${this.escapeHtml(ariaLabel)}">
          <span class="material-icons">${state === 'ready' ? 'edit' : 'add'}</span>
        </button>
      </span>
    `;
  }

  private renderTemplateName(row: any): string {
    const name = this.escapeHtml(row?.displayName || row?.name || this.t('contentTypes.unnamedTemplate', 'Unbenannte Vorlage'));
    return `<span class="template-name-cell"><strong>${name}</strong></span>`;
  }

  private formatSchemaMode(value: string): string {
    const labels: Record<string, string> = {
      warning: this.t('contentTypes.warning', 'Warnung'),
      error: this.t('contentTypes.error', 'Fehler'),
      strict: this.t('contentTypes.strict', 'Streng'),
      disabled: this.t('contentTypes.off', 'Aus'),
      off: this.t('contentTypes.off', 'Aus'),
      standard: this.t('contentTypes.standard', 'Standard')
    };
    return this.escapeHtml(labels[String(value || 'warning').toLowerCase()] || value || this.t('contentTypes.warning', 'Warnung'));
  }

  private renderCoverImage(row: any): string {
    if (!row?.nfcURLPrefix || !row?.id) {
      return `<span class="cover-placeholder" title="${this.escapeHtml(this.t('contentTypes.noCoverImage', 'Kein Titelbild'))}"><span class="material-icons">image_not_supported</span></span>`;
    }
    const src = `${this.urlHost}/v1/coverImage?nfcURLPrefix=${encodeURIComponent(row.nfcURLPrefix)}&contentType=${encodeURIComponent(row.id)}&width=180&v=${this.coverImageRefreshToken}`;
    return `<img class="cover-thumb" src="${this.escapeHtml(src)}" title="${this.escapeHtml(this.t('contentTypes.coverImage', 'Titelbild'))}" onerror="this.style.display='none';this.nextElementSibling.style.display='inline-flex';"><span class="cover-placeholder" style="display:none;" title="${this.escapeHtml(this.t('contentTypes.noCoverImage', 'Kein Titelbild'))}"><span class="material-icons">image_not_supported</span></span>`;
  }

  private escapeHtml(value: string): string {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  private t(key: string, fallback: string): string {
    const translated = this.translate.instant(key);
    return translated && translated !== key ? translated : fallback;
  }

  translateText(key: string, fallback: string): string {
    return this.t(key, fallback);
  }

  private countSchemaFields(nodes: any[]): number {
    return nodes.reduce((count, node) => count + 1 + this.countSchemaFields(node.properties || []), 0);
  }

  private hasDisplayTemplate(contentType: any): boolean {
    return Array.isArray(contentType?.displaySections) && contentType.displaySections.length > 0;
  }

  private getLatestTimestamp(...values: any[]): any {
    return values
      .filter(Boolean)
      .sort((left, right) => this.getTimestampMillis(right) - this.getTimestampMillis(left))[0];
  }

  private getTimestampMillis(value: any): number {
    const date = this.getDateValue(value);
    const time = date?.getTime?.();
    return Number.isFinite(time) ? time : 0;
  }

  private compareDates(left: any, right: any): number {
    return this.getTimestampMillis(left) - this.getTimestampMillis(right);
  }

  private compareDateFilter(filterDate: Date, cellValue: any): number {
    const cellDate = this.getDateValue(cellValue);
    if (!cellDate) return -1;
    const cellDateOnly = new Date(cellDate.getFullYear(), cellDate.getMonth(), cellDate.getDate()).getTime();
    const filterDateOnly = new Date(filterDate.getFullYear(), filterDate.getMonth(), filterDate.getDate()).getTime();
    return cellDateOnly - filterDateOnly;
  }

  private getDateValue(value: any): Date | undefined {
    if (!value) return undefined;
    if (value?.toDate) return value.toDate();
    if (value instanceof Date) return value;
    if (Number.isFinite(value?.seconds)) return new Date(value.seconds * 1000);
    return new Date(value);
  }

  private async readTemplateImport(file: File): Promise<{ contentType?: any; passTemplate?: any; children?: any[] }> {
    if (file.name.toLowerCase().endsWith('.json')) {
      const parsed = JSON.parse(await file.text());
      return {
        contentType: parsed.contentType || parsed.contentype || parsed,
        passTemplate: parsed.passTemplate || parsed.template,
        children: parsed.children || []
      };
    }
    const zip = await JSZip.loadAsync(file);
    const [contentTypeRaw, templateRaw, childrenRaw] = await Promise.all([
      zip.file('contentType.json')?.async('string'),
      zip.file('template.json')?.async('string'),
      zip.file('children.json')?.async('string').catch(() => undefined)
    ]);
    return {
      contentType: contentTypeRaw ? JSON.parse(contentTypeRaw) : undefined,
      passTemplate: templateRaw ? JSON.parse(templateRaw) : undefined,
      children: childrenRaw ? JSON.parse(childrenRaw) : []
    };
  }

  private cleanTemplateExport(value: any, omitKeys: string[] = []): any {
    return JSON.parse(JSON.stringify(value || {}, (key, currentValue) => {
      if (omitKeys.includes(key)) return undefined;
      if (key === 'advancedExperienceId' || key === 'coverImage' || key === 'nfcURLPrefix') return undefined;
      return currentValue;
    }));
  }

  private collectTemplateAssets(passTemplate: any, children: any[], passId: string): any[] {
    const imagesToExport: any = {};
    const exportImages: any[] = [];
    this.collectAssetsFromWidgets(passTemplate?.widgets, passId, imagesToExport, exportImages);
    this.collectAssetsFromTranslations(passTemplate?.translations, passId, imagesToExport, exportImages);
    (children || []).forEach(child => {
      this.collectAssetsFromWidgets(child?.widgets, passId, imagesToExport, exportImages);
      this.collectAssetsFromTranslations(child?.translations, passId, imagesToExport, exportImages);
    });
    if (passTemplate?.theme?.fonts) {
      const fonts = Object.entries(passTemplate.theme.fonts);
      fonts.forEach(font => {
        const name = font[0];
        const fontData = font[1];
        if (fontData && (fontData as any)['url']) {
          let url = deepCopy(supportUrlStructure((fontData as any)['url'], passId, this.settingsService.contextId$));
          if (url) {
            imagesToExport['font-' + name] = url;
            exportImages.push(['font-' + name, url, 'fonts']);
          }
        }
      });
    }
    removeUndefined(imagesToExport);
    return exportImages;
  }

  private collectAssetsFromWidgets(
    widgets: any[],
    passId: string,
    imagesToExport: any,
    exportImages: any[]
  ) {
    widgets?.forEach(widget => {
      const type = widget.type;
      const id = widget.id;
      switch(type){
        case 'billboardImage':{
          if(widget.url){
            const img = deepCopy(supportUrlStructure(widget.url, passId ,this.settingsService.contextId$))
            if(img){
              imagesToExport[id] = img
              exportImages.push([id, img,type])
            }
          }

          if(widget.linkURL){
            let linkurl = deepCopy(supportUrlStructure(widget.linkURL, passId,this.settingsService.contextId$))
            if(linkurl){
              imagesToExport['link-'+id] = linkurl
              exportImages.push(['link-'+id,linkurl,type])
            }
          }
          break;
        }

        case 'billboardVideo':{
          if(widget.url){
            const img = deepCopy(supportUrlStructure(widget.url, passId,this.settingsService.contextId$))
            if(img){
              imagesToExport[id] = img
              exportImages.push([id, img,type])
            }
          }
          break;
        }
        case 'text':{
          if(widget.image && widget.image.url){
            const img = deepCopy(supportUrlStructure(widget.image.url, passId,this.settingsService.contextId$))
            if(img){
              // imagesToExport[id+'_'+type] = img
              // exportImages.push([id+'_'+type, img,type])
              imagesToExport[id] = img
              exportImages.push([id, img,type])
            }
          }

          if(widget.image && widget.image.linkURL){
            let linkurl = deepCopy(supportUrlStructure(widget.image.linkURL, passId,this.settingsService.contextId$))
            if(linkurl){
              imagesToExport['link-'+id] = linkurl
              exportImages.push(['link-'+id, linkurl,type])
            }
          }
          break;
        }

        case 'imagesHorizontal':{
          if(widget.images){
            const images = widget.images
            images.forEach((image,imageIndex) => {
              if(image.url){
                let img = deepCopy(supportUrlStructure(image.url, passId,this.settingsService.contextId$))
                if(img){
                  imagesToExport[id+'.'+imageIndex] = img
                  exportImages.push([id+'.'+imageIndex, img,type])
                }
              }

              if(image.linkURL){
                let linkurl = deepCopy(supportUrlStructure(image.linkURL, passId,this.settingsService.contextId$))
                if(linkurl){
                  imagesToExport['link-'+id+'.'+imageIndex] = linkurl
                  exportImages.push(['link-'+id+'.'+imageIndex, linkurl,type])
                }
              }
            });
          }
          break;
        }
        case 'imageBlocks':{
          if(widget.images){
            const images = widget.images
            images.forEach((image,imageIndex) => {
              if(image.url){
                let img = deepCopy(supportUrlStructure(image.url, passId,this.settingsService.contextId$))
                if(img){
                  imagesToExport[id+'.'+imageIndex] = img
                  exportImages.push([id+'.'+imageIndex, img,type])
                }
              }

              if(image.linkURL){
                let linkurl = deepCopy(supportUrlStructure(image.linkURL, passId,this.settingsService.contextId$))
                if(linkurl){
                  imagesToExport['link-'+id+'.'+imageIndex] = linkurl
                  exportImages.push(['link-'+id+'.'+imageIndex, linkurl,type])
                }
              }
            });
          }
          break;
        }

        case 'images':{
          if(widget.images){
            const images = widget.images
            images.forEach((image,imageIndex) => {
              if(image.url){
                let img = deepCopy(supportUrlStructure(image.url, passId,this.settingsService.contextId$))
                if(img){
                  imagesToExport[id+'.'+imageIndex] = img
                  exportImages.push([id+'.'+imageIndex, img,type])
                }
              }

              if(image.linkURL){
                let linkurl = deepCopy(supportUrlStructure(image.linkURL, passId,this.settingsService.contextId$))
                if(linkurl){
                  imagesToExport['link-'+id+'.'+imageIndex] = linkurl
                  exportImages.push(['link-'+id+'.'+imageIndex, linkurl,type])
                }
              }
            });
          }
          break;
        }

        case 'image':{
          if(widget.url){
            const img = deepCopy(supportUrlStructure(widget.url, passId,this.settingsService.contextId$))
            if(img){
              imagesToExport[id] = img
              exportImages.push([id, img,type])
            }
          }
          if(widget.linkURL){
            let linkurl = deepCopy(supportUrlStructure(widget.linkURL, passId,this.settingsService.contextId$))
            if(linkurl){
              imagesToExport['link-'+id] = linkurl
              exportImages.push(['link-'+id, linkurl,type])
            }
          }
          break;
        }
        case 'buttons':{
          if(widget.buttons){
            const buttons = widget.buttons
            buttons.forEach((btn, indexBtn) => {
              if(btn.url){
                let img = deepCopy(supportUrlStructure(btn.url, passId,this.settingsService.contextId$))
                // btn.url = img
                if(img){
                  imagesToExport[id+'.'+indexBtn] = img
                  exportImages.push([id+'.'+indexBtn, img,type])
                }
              }
            });
          }
          break;
        }
        case 'annotatedMap':{
          if(widget.annotations){
            const annotations = widget.annotations
            annotations.forEach((annotation, indexPin) => {
              const linkURL = annotation.dynamic ? (annotation?.properties?.linkURL ? annotation.properties.linkURL : undefined) : annotation.linkURL
              if(linkURL){
                let linkurl = deepCopy(supportUrlStructure(linkURL, passId,this.settingsService.contextId$))
                // annotation.linkURL = linkurl
                if(linkurl){
                  imagesToExport['link-'+id+'.'+indexPin] = linkurl
                  exportImages.push(['link-'+id+'.'+indexPin, linkurl,type])
                }
              }
            });
          }
          break;
        }
        case 'video':{
          if(widget.url){
            const img = deepCopy(supportUrlStructure(widget.url, passId,this.settingsService.contextId$))
            if(img){
              imagesToExport[id] = img
              exportImages.push([id, img,type])
            }
          }
          break;
        }
        case 'callToAction':{
          if(widget.imageUrl){
            let img =  deepCopy(supportUrlStructure(widget.imageUrl, passId,this.settingsService.contextId$))
            // widget.imageUrl = img
            if(img){
              imagesToExport[id] = img
              exportImages.push([id, img,type])
            }
          }
          break;
        }
        case 'splashVideo':{
          if(widget.url){
            const img = deepCopy(supportUrlStructure(widget.url, passId,this.settingsService.contextId$))
            if(img){
              imagesToExport[id] = img
              // widget.url = img
              exportImages.push([id, img,type])
            }
          }
          break;
        }

        case 'spacer':{
          break;
        }

        case 'form':{
          break;
        }

        case 'list':{
          break;
        }

        case 'products':{
          break;
        }

        case '3dmodel':{
          const img = deepCopy(supportUrlStructure(widget.url, passId,this.settingsService.contextId$))
          if(img){
            imagesToExport[id] = img
            exportImages.push([id, img,type])
          }
          break;
        }

        case 'countdown':{
          if(widget.targetDate){
            const date = new Date( isDateTimeGetTimeSeconds(widget.targetDate))
            widget.targetDate = date
            const seconds = Math.floor(date.getTime() / 1000);
            const nanoseconds = (date.getMilliseconds() * 1000000);
            const timestampJson = {
              _seconds: seconds,_nanoseconds: nanoseconds
            }
            widget.targetDate = timestampJson
          }
          break;
        }

        case 'composed':{
          break;
        }

        case 'productRegistration':{
          if(widget.image && widget.image.url){
            const img = deepCopy(supportUrlStructure(widget.image.url, passId,this.settingsService.contextId$))
            if(img){
              imagesToExport[id] = img
              exportImages.push([id, img,type])
            }
          }
        }
      }
    });
  }

  private collectAssetsFromTranslations(
    translations: any,
    passId: string,
    imagesToExport: any,
    exportImages: any[]
  ) {
    if (!translations || typeof translations !== 'object') return;

    Object.keys(translations).forEach((lang) => {
      const langObj = translations[lang];
      if (!langObj || typeof langObj !== 'object') return;

      Object.keys(langObj).forEach((key) => {
        const v = langObj[key];
        if (typeof v !== 'string') return;

        // only media (translated images)
        if (!v.startsWith('pass://')) return;

        const url = deepCopy(
          supportUrlStructure(v, passId, this.settingsService.contextId$)
        );

        if (url) {
          // IMPORTANT: filename must include widgetId so importer can still "see" the widget id
          // also avoid '-' parsing problems (pt-BR etc) by using "__"
          const fileKey = `tr__${lang}__${key}`;

          imagesToExport[fileKey] = url;
          exportImages.push([fileKey, url, 'translations']);
        }
      });
    });
  }

  private async addAssetsToZip(zip: JSZip, assets: any[]): Promise<void> {
    if (!assets.length) return;
    const token = await this.appCheckService.getToken();
    const headers: Record<string, string> = {};
    if (token) headers['X-Firebase-AppCheck'] = token;
    await Promise.all(assets.map(async (asset) => {
      const name = asset[0];
      const url = asset[1];
      try {
        const blob = await firstValueFrom(this.http.get(url, { responseType: 'blob', headers }));
        zip.file(name, blob);
      } catch (error) {
        console.warn('Asset could not be added to template export', name, url, error);
      }
    }));
  }

  private sanitizeFileName(value: string): string {
    return value.trim().replace(/[^a-z0-9äöüß_-]+/gi, '_').replace(/^_+|_+$/g, '') || 'dpp-vorlage';
  }

  private formatDate(value: any): string {
    const date = this.getDateValue(value);
    return date && !Number.isNaN(date.getTime()) ? date.toLocaleString() : '';
  }
}
